import { useQuery } from '@tanstack/react-query';

import { atsIntegrationsClient } from 'clients/ATSIntegrationsClient/ATSIntegrationsClient';
import { AtsStatus } from 'clients/ATSIntegrationsClient/types';
import { QueryKey } from 'types/query';
import { OptionType } from 'shared/legacy/components/Select';

export const ALLOWED_ATS_CODE_NAMES = ['mysolution', 'otys', 'carerix', 'tigris'];

export const allowedStatuses = {
  mysolution: {
    canResync: false,
    statuses: [
      {
        id: 'Sollicitatie vragen beantwoord',
        ats_status_name: 'Sollicitatie vragen beantwoord',
        ats_status_id: 'Sollicitatie vragen beantwoord',
        is_rejected: false,
      },
      {
        id: 'Benaderd',
        ats_status_name: 'Benaderd',
        ats_status_id: 'Benaderd',
        is_rejected: false,
      },
      {
        id: 'Presented',
        ats_status_name: 'Voorgesteld',
        ats_status_id: 'Presented',
        is_rejected: false,
      },
      {
        id: 'Hired',
        ats_status_name: 'Aangenomen',
        ats_status_id: 'Hired',
        is_rejected: false,
      },
      {
        id: 'Match',
        ats_status_name: 'Match',
        ats_status_id: 'Match',
        is_rejected: false,
      },
      {
        id: 'Application',
        ats_status_name: 'Application',
        ats_status_id: 'Application',
        is_rejected: false,
      },
    ],
  },
  otys: {
    canResync: true,
  },
  carerix: {
    canResync: false,
  },
  tigris: {
    canResync: true,
  },
};

export const fetchAtsApplicationStatuses = async (
  tenantId: number | undefined,
  atsCodeName: string | undefined,
): Promise<AtsStatus[] | undefined> => {
  if (!tenantId || !atsCodeName) return [];

  if (atsCodeName === 'mysolution') {
    return allowedStatuses.mysolution.statuses;
  }

  const response = await atsIntegrationsClient.getStatuses(tenantId, atsCodeName, 'application');

  return response.data;
};

export const useGetAtsApplicationStatuses = (
  tenantId: number | undefined,
  atsCodeName: string | undefined,
  isPullingApplicationStatuses: boolean,
  ats: any,
) => {
  // For mysolution, statuses are hardcoded, so we can enable the query as soon as atsCodeName is available
  // For other ATS types, we need to wait for ats to be loaded
  const isMysolution = atsCodeName === 'mysolution';
  const shouldEnable = !isPullingApplicationStatuses && (isMysolution ? !!atsCodeName && !!tenantId : !!ats);

  return useQuery(
    [QueryKey.atsApplicationStatusesQuery, tenantId, atsCodeName],
    () => fetchAtsApplicationStatuses(tenantId, atsCodeName),
    {
      enabled: shouldEnable,
    },
  );
};

export const getAtsStatusName = (statusId: string | number | undefined, atsCodeName: string | undefined): string => {
  if (!statusId || !atsCodeName) return String(statusId || '');

  if (atsCodeName === 'mysolution') {
    const status = allowedStatuses.mysolution.statuses.find((s) => s.id === statusId);
    return status?.ats_status_name || String(statusId);
  }

  return String(statusId);
};

type StatusFormatter = (statuses: AtsStatus[]) => OptionType[];

const mysolutionFormatter: StatusFormatter = (statuses: AtsStatus[]) => {
  return statuses.map((status) => ({
    title: status.ats_status_name,
    value: String(status.id),
  }));
};

const tigrisFormatter: StatusFormatter = (statuses: AtsStatus[]) => {
  return statuses.map((status) => ({
    title: status.ats_status_name,
    value: status.ats_status_name,
  }));
};

const defaultFormatter: StatusFormatter = (statuses: AtsStatus[]) => {
  return statuses
    .filter((status) => status.ats_status_id != null)
    .map((status) => ({
      title: status.ats_status_name,
      value: String(status.ats_status_id),
    }));
};

const statusFormatters: Record<string, StatusFormatter> = {
  mysolution: mysolutionFormatter,
  tigris: tigrisFormatter,
};

export const formatAtsStatuses = (atsCodeName: string | undefined, statuses: AtsStatus[] | undefined): OptionType[] => {
  if (!statuses) return [];

  const formatter = atsCodeName ? statusFormatters[atsCodeName] : undefined;
  const selectedFormatter = formatter || defaultFormatter;

  return selectedFormatter(statuses);
};
